import { useEffect, useState } from "react";
import {
    Box,
    Card,
    CircularProgress,
    Button,
    Grid,
} from "@mui/material";
import Scrollbar from "src/components/scrollbar/Scrollbar";
import { get, put } from "src/utils/networkUtils";
import LevelTable from "src/components/levelUnloks/levelTable";
import AddIcon from "@mui/icons-material/Add";
import { getUserPermissions } from "src/utils/permissionsUtils";

const DAILY_WAIT_OPTIONS = ["8hr", "10hr", "12hr", "24hr"];
const TRAINING_WAIT_OPTIONS = ["4hrs", "6hrs", "8hrs", "12hrs", "24hrs"];

export default function LevelUnlocksContent() {
    const [xpLevelSettings, setXpLevelSettings] = useState([]);
    const [originalSettings, setOriginalSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [storePackOptions, setStorePackOptions] = useState([]);
    const [trainingLevelOptions, setTrainingLevelOptions] = useState([]);
    const [, setPackPullTypes] = useState([]);
    const [packPullError, setPackPullError] = useState({});
    const [coinsPullError, setCoinsPullError] = useState({});
    const [modifiedLevels, setModifiedLevels] = useState(new Set());
    const [selectedCardType, setSelectedCardType] = useState({});
    const [editingStartIndex, setEditingStartIndex] = useState(null);
    const [editingEndIndex, setEditingEndIndex] = useState(null);
    const { isViewer } = getUserPermissions();

    useEffect(() => {
        get(`/admin/xp-level-settings`)
            .then((response) => {
                const levels = response.settings || [];
                const levelsWithCombined = levels.map((lvl) => {
                    const trainingLevels = lvl.settings?.Training?.TrainingLevels || [];
                    const trainingSlots = lvl.settings?.Training?.TrainingSlots || [];
                    const combined = trainingLevels.map(
                        (level, i) => `${level} - ${trainingSlots[i] || ""}`
                    );
                    return {
                        ...lvl,
                        settings: {
                            ...lvl.settings,
                            Training: {
                                ...lvl.settings.Training,
                                Combined: combined,
                            },
                        },
                    };
                });

                setXpLevelSettings(levelsWithCombined);
                setOriginalSettings(levelsWithCombined);
                setOriginalSettings(levels);

                const allPacks = new Set();
                const allTrainingLevels = new Set();
                const allPackPullTypes = new Set();

                levelsWithCombined.forEach((lvl, index) => {
                    lvl.settings?.Packs?.Available?.forEach((p) => allPacks.add(p));
                    lvl.settings?.Training?.TrainingLevels?.forEach((t) =>
                        allTrainingLevels.add(t)
                    );
                    Object.keys(lvl.settings?.DailyFreePack?.CardPull || {}).forEach(
                        (k) => allPackPullTypes.add(k)
                    );

                    validateCardPullSum(index, levelsWithCombined);
                    validateCoinsPullSum(index, levelsWithCombined);
                });

                setStorePackOptions([...allPacks]);
                setTrainingLevelOptions([...allTrainingLevels]);
                setPackPullTypes([...allPackPullTypes]);
            })
            .catch((err) => {
                console.error("xp-level-settings:", err);
            })
            .finally(() => setLoading(false));
    }, []);

    const handleChange = (index, path, value) => {
        setXpLevelSettings((prevSettings) => {
            const newSettings = [...prevSettings];
            const keys = path.split(".");

            let obj = { ...newSettings[index] };
            let current = obj;

            for (let i = 0; i < keys.length - 1; i++) {
                current[keys[i]] = { ...current[keys[i]] };
                current = current[keys[i]];
            }

            const lastKey = keys[keys.length - 1];

            if (Array.isArray(current[lastKey]) || Array.isArray(value)) {
                current[lastKey] = value;
            } else {
                current[lastKey] = Number(value || 0);
            }

            if (lastKey === "rewardStart" || lastKey === "rewardEnd") {
                if (obj.rewardEnd < obj.rewardStart) {
                    obj.rewardEnd = obj.rewardStart;
                }

                if (lastKey === "rewardEnd" && newSettings[index + 1]) {
                    newSettings[index + 1].rewardStart = obj.rewardEnd + 1;
                    if (
                        newSettings[index + 1].rewardEnd <
                        newSettings[index + 1].rewardStart
                    ) {
                        newSettings[index + 1].rewardEnd =
                            newSettings[index + 1].rewardStart;
                    }
                }

                if (lastKey === "rewardStart" && index > 0) {
                    newSettings[index - 1].rewardEnd = obj.rewardStart - 1;
                    if (
                        newSettings[index - 1].rewardEnd <
                        newSettings[index - 1].rewardStart
                    ) {
                        newSettings[index - 1].rewardStart =
                            newSettings[index - 1].rewardEnd;
                    }
                }
            }

            if (path.startsWith("settings.DailyFreePack.CardPull")) {
                validateCardPullSum(index, newSettings);
            }
            if (path.startsWith("settings.DailyFreePack.CoinsPull")) {
                validateCoinsPullSum(index, newSettings);
            }

            newSettings[index] = obj;

            return newSettings;
        });

        setModifiedLevels((prev) => new Set(prev.add(index)));
    };

    const handleSave = () => {
        setLoading(true);

        const levelsToSave = xpLevelSettings.filter((_, index) =>
            modifiedLevels.has(index)
        );

        const promises = levelsToSave.map((level) => {
            const payload = {
                levelId: level.levelId,
                settings: level.settings,
                rewardStart: level.rewardStart,
                rewardEnd: level.rewardEnd,
            };
            return put(`/admin/xp-level-settings`, payload);
        });

        Promise.all(promises)
            .then(() => get(`/admin/xp-level-settings`))
            .then((response) => {
                setXpLevelSettings(response.settings || []);
                setOriginalSettings(response.settings || []);
                setModifiedLevels(new Set());
            })

            .catch((err) => {
                console.error("Error saving changes:", err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const validateCoinsPullSum = (index, settings) => {
        const coinsPull = settings[index].settings?.DailyFreePack?.CoinsPull || {};
        const sum = Object.values(coinsPull).reduce(
            (acc, val) => acc + Number(val || 0),
            0
        );
        setCoinsPullError((prev) => ({
            ...prev,
            [index]:
                sum !== 100
                    ? `The total coins probability is ${sum}%. It must be exactly 100%.`
                    : null,
        }));
    };

    const validateCardPullSum = (index, settings) => {
        const cardPull = settings[index].settings?.DailyFreePack?.CardPull || {};
        const sum = Object.values(cardPull).reduce(
            (acc, val) => acc + Number(val || 0),
            0
        );
        setPackPullError((prev) => ({
            ...prev,
            [index]:
                sum !== 100
                    ? `The total probability is ${sum}%. It must be exactly 100%.`
                    : null,
        }));
    };

    const handleAddCardRow = (index) => {
        const newSettings = [...xpLevelSettings];

        if (!newSettings[index].settings.DailyFreePack.CardPull) {
            newSettings[index].settings.DailyFreePack.CardPull = {};
        }

        const cardPull = newSettings[index].settings.DailyFreePack.CardPull;
        const existingKeys = Object.keys(cardPull);
        const defaultTypes = ["Gold", "Silver", "Base", "AllStar"];

        const nextType = defaultTypes.find((type) => !existingKeys.includes(type));
        if (!nextType) return;

        cardPull[nextType] = 0;

        setXpLevelSettings(newSettings);
    };

    const handleAddCoinRow = (index) => {
        const newSettings = [...xpLevelSettings];
        if (!newSettings[index].settings.DailyFreePack.CoinsPull) {
            newSettings[index].settings.DailyFreePack.CoinsPull = {};
        }

        newSettings[index].settings.DailyFreePack.CoinsPull[""] = 0;

        setXpLevelSettings(newSettings);
    };

    const handleDeleteRow = (index, type, key) => {
        const newSettings = [...xpLevelSettings];
        if (type === "Card") {
            delete newSettings[index].settings.DailyFreePack.CardPull[key];
            validateCardPullSum(index, newSettings);
        } else if (type === "Coin") {
            delete newSettings[index].settings.DailyFreePack.CoinsPull[key];
        }
        setXpLevelSettings(newSettings);
        validateCoinsPullSum(index, newSettings);
    };
    const handleCancel = () => {
        setXpLevelSettings(originalSettings);
        setModifiedLevels(new Set());
    };

    const handleAddXpLevel = () => {
        const newLevelIndex = xpLevelSettings.length;
        const newLevel = {
            levelId: newLevelIndex + 1,
            levelName: `${newLevelIndex + 1}`,
            settings: {
                DailyFreePack: {
                    CardPull: {
                        Gold: "",
                        Silver: "",
                        Base: "",
                        AllStar: "",
                    },
                    CoinsPull: {
                        20: "",
                        40: "",
                        100: "",
                    },
                    PackFrequencyHrs: "",
                },
                Packs: {
                    Available: [],
                },
                Training: {
                    Combined: [],
                    TrainingSlots: [],
                    TrainingDurationHrs: "",
                },
            },
        };

        setXpLevelSettings((prev) => [...prev, newLevel]);
        setModifiedLevels((prev) => {
            const updated = new Set(prev);
            updated.add(newLevelIndex);
            return updated;
        });
    };

    return (
        <Box sx={{ p: 0 }}>
            <Grid item>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                        marginBottom: 5,
                    }}
                >
                    <Button variant="outlined" color="secondary" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={Object.values(packPullError).some((v) => v)}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </Box>
            </Grid>

            <Card>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Scrollbar>
                        <LevelTable
                            xpLevelSettings={xpLevelSettings}
                            storePackOptions={storePackOptions}
                            trainingLevelOptions={trainingLevelOptions}
                            DAILY_WAIT_OPTIONS={DAILY_WAIT_OPTIONS}
                            TRAINING_WAIT_OPTIONS={TRAINING_WAIT_OPTIONS}
                            packPullError={packPullError}
                            coinsPullError={coinsPullError}
                            handleChange={handleChange}
                            handleDeleteRow={handleDeleteRow}
                            handleAddCardRow={handleAddCardRow}
                            handleAddCoinRow={handleAddCoinRow}
                            selectedCardType={selectedCardType}
                            setSelectedCardType={setSelectedCardType}
                            setXpLevelSettings={setXpLevelSettings}
                            setModifiedLevels={setModifiedLevels}
                            originalSettings={originalSettings}
                            editingStartIndex={editingStartIndex}
                            setEditingStartIndex={setEditingStartIndex}
                            editingEndIndex={editingEndIndex}
                            setEditingEndIndex={setEditingEndIndex}
                            isViewer={isViewer}
                        />
                    </Scrollbar>
                )}
            </Card>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    marginTop: 3,
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: "10px", height: 52, minWidth: 200 }}
                    startIcon={<AddIcon />}
                    onClick={handleAddXpLevel}
                >
                    Add XP Level
                </Button>
            </Box>
        </Box>
    );
}
